"use client";

import { useState, useEffect } from "react";
import SpotlightCard from "./SpotlightCard.client";
import SpotlightLivingCard from "./SpotlightLivingCard";
import { SpotlightItem } from "@/data/spotlight";
import { sortSpotlightItems, sortSpotlightItemsRandomized } from "@/lib/sortUtils";

interface SpotlightGridProps {
  spotlight: SpotlightItem[];
  initialFilters?: Record<string, string | null>;
  filters?: Record<string, string | null>;
  maxRows?: number;
  showAll?: boolean;
  locale: string;
}

export default function SpotlightGrid({
  spotlight,
  initialFilters = {},
  filters = {},
  maxRows = 3,
  showAll = false,
  locale
}: SpotlightGridProps) {
  const [activeFilters, setActiveFilters] = useState<Record<string, string | null>>(initialFilters);
  const [filteredSpotlight, setFilteredSpotlight] = useState<SpotlightItem[]>([]);
  const [displayedItems, setDisplayedItems] = useState<SpotlightItem[]>([]);

  // Update active filters if the filters prop changes
  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      setActiveFilters(filters);
    }
  }, [filters]);

  // Create a stable representation of filters to use in dependency array
  const filtersString = JSON.stringify(activeFilters);

  // Apply filters whenever spotlight or filters change
  useEffect(() => {
    // Filter by locale first (should always show content for current locale or without locale specified)
    let filtered = spotlight.filter(
      item => !item.locale || item.locale.includes(locale)
    );

    // Then apply any additional tag filters
    // Get active filters (non-null values)
    const activeFilterPairs = Object.entries(activeFilters)
      .filter(([, value]) => value !== null)
      .map(([key, value]) => `${key}:${value}`);

    if (activeFilterPairs.length > 0) {
      filtered = filtered.filter(item => {
        return activeFilterPairs.every(filterPair => {
          const filterValue = filterPair.split(':')[1];
          return item.tags.includes(filterValue);
        });
      });
    }

    setFilteredSpotlight(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlight, locale, filtersString]);

  // Process the filtered items to get the displayed items
  useEffect(() => {
    if (filteredSpotlight.length === 0) {
      setDisplayedItems([]);
      return;
    }

    // Sort items using appropriate sorting logic
    // On home page: "Home"-tagged items first, then randomized rest
    let sortedItems: SpotlightItem[];
    if (showAll) {
      sortedItems = sortSpotlightItems(filteredSpotlight);
    } else {
      const homeItems = filteredSpotlight.filter(item => item.tags.includes('Home'));
      const otherItems = filteredSpotlight.filter(item => !item.tags.includes('Home'));
      sortedItems = [...homeItems, ...sortSpotlightItemsRandomized(otherItems)];
    }
    
    // Apply row limits unless showAll is true
    const items = showAll ? sortedItems : limitSpotlightByRows(sortedItems, maxRows);
    
    // Set displayed items immediately without delay or loading state
    setDisplayedItems(items);
  }, [filteredSpotlight, maxRows, showAll]);

  
  // Calculate how many spotlight items we can show based on maxRows
  const limitSpotlightByRows = (items: SpotlightItem[], maxRows: number): SpotlightItem[] => {
    const sorted = [...items];
    
    // Create a virtual grid to track what cells are filled
    // We use a 4-column grid (based on our grid-template-columns setting)
    const GRID_COLUMNS = 4;
    const grid: boolean[][] = Array(maxRows).fill(0).map(() => Array(GRID_COLUMNS).fill(false));
    
    // Function to check if a card fits at a position
    const canPlaceCard = (row: number, col: number, rowSpan: number, colSpan: number): boolean => {
      // Check if out of bounds
      if (row + rowSpan > maxRows || col + colSpan > GRID_COLUMNS) {
        return false;
      }
      
      // Check if any cell is already occupied
      for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + colSpan; c++) {
          if (grid[r][c]) return false;
        }
      }
      
      return true;
    };
    
    // Function to place a card on the grid
    const placeCard = (row: number, col: number, rowSpan: number, colSpan: number) => {
      for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + colSpan; c++) {
          grid[r][c] = true;
        }
      }
    };
    
    // Try to place each card in the grid
    const limited: SpotlightItem[] = [];
    
    for (const item of sorted) {
      const size = item.size || "1x1";
      let rowSpan = 1;
      let colSpan = 1;
      
      // Determine spans based on card size
      if (size === "1x2" || size === "2x2") rowSpan = 2;
      if (size === "2x1" || size === "2x2") colSpan = 2;
      
      // Try to find a place for this card
      let placed = false;
      
      // Check each possible position
      for (let row = 0; row < maxRows && !placed; row++) {
        for (let col = 0; col < GRID_COLUMNS && !placed; col++) {
          if (canPlaceCard(row, col, rowSpan, colSpan)) {
            // Place the card here
            placeCard(row, col, rowSpan, colSpan);
            limited.push(item);
            placed = true;
          }
        }
      }
    }
    
    return limited;
  };

  // Helper to determine grid span classes based on card size
  const getGridClasses = (item: SpotlightItem): string => {
    const size = item.size || "1x1";
    
    switch (size) {
      case "1x1":
        return "";
      case "2x1":
        return "col-span-2";
      case "1x2":
        return "row-span-2";
      case "2x2":
        return "col-span-2 row-span-2";
      default:
        return "";
    }
  };

  // Determine which CSS class to use based on whether we're showing all items
  const gridClassName = showAll
    ? "grid-template-spotlight-full" // Use a special class for full view
    : "grid-template-spotlight"; // Use the standard grid for row-limited view

  // If no data at all, show message
  if (filteredSpotlight.length === 0) {
    return (
      <div className="col-span-full text-center py-8 text-muted-foreground">
        No spotlight items match the selected filters.
      </div>
    );
  }

  return (
    <div className={gridClassName}>
      {displayedItems.map((item, index) => (
        <div
          key={`spotlight-${index}`}
          className={getGridClasses(item)}
        >
          <SpotlightLivingCard
            spotlight={item}
            id={`spotlight-${index}`}
            className="w-full"
          >
            <SpotlightCard
              spotlight={item}
              className="w-full"
            />
          </SpotlightLivingCard>
        </div>
      ))}
    </div>
  );
}